//
/* eslint-disable */
import React from "react"
import {
    allRegionsExceptUS,
    internationalWithoutBillsRegions,
    notInternationalEURegions,
} from "shared/imports/regionConstants"

import {
    checkAllowedRegion,
    checkDoesNotHaveAnyRegion,
    checkHasAllPermissions,
    checkHasRegion,
    checkHasUserPermission,
    permissionFilter,
} from "shared/imports/permissionFunctions"

import { LEADERSHIP_CONNECT_ORG_ID, MEDIA_FLV, OFFICIAL_FLV, STAFFER_FLV } from "shared/imports/sharedConstants"
import { sortBySearchOrder } from "shared/imports/sharedHelperFunctions"
import { kseFilter, msaDatasetFilter, ymcaDatasetFilter } from "shared/search/helperFunctions"

import { isAnyFeatureEnabled, isFeatureEnabled } from "shared/featureflags/helperFunctions"
import {
    AlertFeedItemFilterableListView,
    AmendmentFilterableListView,
    ArticleFilterableListView,
    BillFilterableListView,
    BulkEmailFilterableListView,
    BulkSMSFilterableListView,
    CampaignFilterableListView,
    CampaignFinanceDisbursementsFilterableListView,
    CampaignFinanceReceiptsFilterableListView,
    CaucusFilterableListView,
    CommitteeFilterableListView,
    CustomDataSearchifyFLV,
    // CustomEventParticipantFilterableListView,
    DistrictSearchifyFLV,
    DocketFilterableListView,
    DocumentFilterableListView,
    ElectionFilterableListView,
    EventFilterableListView,
    FileFilterableListView,
    GeoShapeSearchifyFLV,
    GrassrootsActionFilterableListView,
    IssueManagementFilterableListView,
    NoteFilterableListView,
    OfficialFilterableListView,
    OfficialsAndStaffEUFilterableListView,
    PacClassicPacOfficeFilterableListView,
    PacClassicPacOrganizationFilterableListView,
    PacClassicPacTransactionFilterableListView,
    PoliticalCommitteeFilterableListView,
    PressContactFilterableListView,
    PublicOrganizationFilterableListView,
    RegulationCommentFilterableListView,
    RegulationFilterableListView,
    SendEmailArchivedSearchifyFLV,
    SendEmailFilterableListView,
    StafferFilterableListView,
    StateSearchifyFLV,
    StatisticFilterableListView,
    SupporterFilterableListView,
    TrackingDashboardFilterableListView,
    TransactionFilterableListView,
    UserFilterableListView,
    VoteFilterableListView,
    SendSMSFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

/*
REQUIRED ITEMS:
- filterableListViewComponent: the react component that specifies inline, filters, etc
- uniqueFlvId: unique string identifying the filterable list view that is attained
    by segueing from the SearchLandingPage
- quorumDataType: quorumDataType of the filterable list view (used for filtering / searchDataTypeBox,
    not used by the filterable list view itself)

OPTIONAL:
- label: The label that will be displayed on the box, if not quorumDataType.plural
- regions: list of numbers representing regions the item should appear in. (edge case for officials/staff)
- excludedByRegions: list of numbers representing regions the item should NOT appear in. (edge case for officials/staff in Global (aka both US and EU))
*/
const searchPageOptions = [
    {
        label: "Officials",
        filterableListViewComponent: OfficialFilterableListView,
        uniqueFlvId: OFFICIAL_FLV,
        quorumDataType: DjangIO.app.models.QuorumDataType.person,
        // excluded since will use "Officials & Staff" in case that current regions includes international regions
        excludedByRegions: allRegionsExceptUS,
    },
    {
        filterableListViewComponent: StafferFilterableListView,
        uniqueFlvId: STAFFER_FLV,
        quorumDataType: DjangIO.app.models.QuorumDataType.staffer,
        // excluded since will use "Officials & Staff" in case that current regions spans international regions and US
        excludedByRegions: allRegionsExceptUS,
    },
    {
        label: DjangIO.app.models.QuorumDataType.person.plural,
        filterableListViewComponent: OfficialsAndStaffEUFilterableListView,
        uniqueFlvId: "OfficialsAndStaffEUFilterableListViewDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.person,
        // should use this if current regions includes Region.eu
        regions: allRegionsExceptUS,
    },
    ...(checkHasAllPermissions([DjangIO.app.models.PermissionType.legislative_data])
        ? [
              {
                  label: DjangIO.app.models.QuorumDataType.bill.plural,
                  filterableListViewComponent: BillFilterableListView,
                  uniqueFlvId: "BillDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.bill,
                  excludedByRegions: checkHasRegion([DjangIO.app.models.Region.global_region.value])
                      ? []
                      : internationalWithoutBillsRegions,
              },
          ]
        : []),
    {
        filterableListViewComponent: DocumentFilterableListView,
        uniqueFlvId: "DocumentDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.document,
    },
    {
        filterableListViewComponent: NoteFilterableListView,
        uniqueFlvId: "NoteDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.note,
    },
    {
        label: DjangIO.app.models.QuorumDataType.tracking_dashboard.plural,
        filterableListViewComponent: TrackingDashboardFilterableListView,
        uniqueFlvId: "TrackingDashboardDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.tracking_dashboard,
        mobileOnly: true,
    },
    {
        label: "Grassroots",
        filterableListViewComponent: GrassrootsActionFilterableListView,
        uniqueFlvId: "GrassrootsActionDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.grassroots_action,
    },
    {
        filterableListViewComponent: SupporterFilterableListView,
        uniqueFlvId: "SupporterDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.supporter,
    },
    {
        filterableListViewComponent: BulkSMSFilterableListView,
        uniqueFlvId: "BulkSMSDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.bulk_sms,
    },
    {
        filterableListViewComponent: SendSMSFilterableListView,
        uniqueFlvId: "SendSMSDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.send_sms,
    },
    {
        mobileLabel: "Orgs",
        filterableListViewComponent: PublicOrganizationFilterableListView,
        uniqueFlvId: "OrganizationDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.public_organization,
    },
    {
        filterableListViewComponent: CommitteeFilterableListView,
        uniqueFlvId: "CommitteeDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.committee,
    },
    {
        filterableListViewComponent: CaucusFilterableListView,
        uniqueFlvId: "CaucusDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.caucus,
    },
    {
        filterableListViewComponent: EventFilterableListView,
        uniqueFlvId: "EventDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.event,
        regions: notInternationalEURegions,
    },
    {
        filterableListViewComponent: VoteFilterableListView,
        uniqueFlvId: "VoteDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.vote,
    },
    {
        filterableListViewComponent: AmendmentFilterableListView,
        uniqueFlvId: "AmendmentDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.amendment,
    },
    {
        filterableListViewComponent: FileFilterableListView,
        uniqueFlvId: "FileDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.attachment,
    },
    {
        label: DjangIO.app.models.QuorumDataType.issue_management.plural,
        filterableListViewComponent: IssueManagementFilterableListView,
        uniqueFlvId: "IssuesManagementDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.issue_management,
        mobileOnly: true,
    },
    {
        label: "Outbox",
        filterableListViewComponent: BulkEmailFilterableListView,
        uniqueFlvId: "BulkEmailSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.bulk_email,
        mobileOnly: true,
    },
    {
        label: "Outbox (Recipients)",
        filterableListViewComponent: SendEmailFilterableListView,
        uniqueFlvId: "SendEmailDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.send_email,
    },
    ...(isFeatureEnabled("ff_send_email_archived_flv")
        ? [
              {
                  label: "Outbox (Recipients) Archived",
                  filterableListViewComponent: SendEmailArchivedSearchifyFLV,
                  uniqueFlvId: "SendEmailDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.send_email_archived,
              },
          ]
        : []),
    {
        filterableListViewComponent: CampaignFilterableListView,
        uniqueFlvId: "CampaignSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.campaign,
        mobileOnly: true,
    },
    {
        label: "Alerts",
        filterableListViewComponent: AlertFeedItemFilterableListView,
        uniqueFlvId: "AlertDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.alert,
    },
    {
        filterableListViewComponent: RegulationFilterableListView,
        uniqueFlvId: "RegulationDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.regulation,
    },
    ...(isFeatureEnabled("ff_regcomment_flv_enabled")
        ? [
              {
                  filterableListViewComponent: RegulationCommentFilterableListView,
                  uniqueFlvId: "RegulationCommentDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.reg_comment,
              },
          ]
        : []),
    {
        filterableListViewComponent: DocketFilterableListView,
        uniqueFlvId: "DocketDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.docket,
    },
    ...(checkHasAllPermissions([DjangIO.app.models.PermissionType.qpac_v1])
        ? [
              {
                  filterableListViewComponent: TransactionFilterableListView,
                  uniqueFlvId: "PacTransactionDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.transaction,
              },
          ]
        : []),
    ...(checkHasAllPermissions([DjangIO.app.models.PermissionType.pac_hybrid]) &&
    checkHasUserPermission(["can_access_pac"])
        ? [
              {
                  filterableListViewComponent: PacClassicPacOfficeFilterableListView,
                  uniqueFlvId: "PacClassicPacOfficeDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.pacc_pac_office,
              },
              // Conditionally include PAC Organizations and Transactions based on PAC 2.0 permissions
              ...(!isFeatureEnabled("ff_permissions_for_pac_2") || Userdata.can_access_pac_2_data
                  ? [
                        {
                            filterableListViewComponent: PacClassicPacOrganizationFilterableListView,
                            uniqueFlvId: "PacClassicPacOrganizationDefaultSearch",
                            quorumDataType: DjangIO.app.models.QuorumDataType.pacc_pac_organization,
                        },
                        {
                            filterableListViewComponent: PacClassicPacTransactionFilterableListView,
                            uniqueFlvId: "PacClassicPacTransactionDefaultSearch",
                            quorumDataType: DjangIO.app.models.QuorumDataType.pacc_pac_transaction,
                        },
                    ]
                  : []),
          ]
        : []),
    ...(checkHasAllPermissions([
        DjangIO.app.models.PermissionType.campaign_finance,
        DjangIO.app.models.PermissionType.can_access_campaign_finance,
    ])
        ? [
              {
                  filterableListViewComponent: CampaignFinanceReceiptsFilterableListView,
                  uniqueFlvId: "ReceiptDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.receipt,
              },
          ]
        : []),
    ...(checkHasAllPermissions([
        DjangIO.app.models.PermissionType.campaign_finance,
        DjangIO.app.models.PermissionType.can_access_campaign_finance,
    ])
        ? [
              {
                  filterableListViewComponent: CampaignFinanceDisbursementsFilterableListView,
                  uniqueFlvId: "DisbursementDefaultSearch",
                  quorumDataType: DjangIO.app.models.QuorumDataType.disbursement,
              },
          ]
        : []),
    {
        filterableListViewComponent: ArticleFilterableListView,
        uniqueFlvId: MEDIA_FLV,
        quorumDataType: DjangIO.app.models.QuorumDataType.media,
    },
    {
        filterableListViewComponent: PressContactFilterableListView,
        uniqueFlvId: "PressContactFilterableListViewDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.press_contact,
    },
    {
        filterableListViewComponent: PoliticalCommitteeFilterableListView,
        uniqueFlvId: "PoliticalCommitteeDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.political_committee,
    },
    {
        filterableListViewComponent: ElectionFilterableListView,
        uniqueFlvId: "ElectionDefaultSearch",
        quorumDataType: DjangIO.app.models.QuorumDataType.election,
    },
    // TODO: uncomment this when we determine that it should be added to search
    // {
    //     filterableListViewComponent: AssignmentFilterableListView,
    //     uniqueFlvId: "AssignmentDefaultSearch",
    //     quorumDataType: DjangIO.app.models.QuorumDataType.assignment,
    // },
]

export const ancillaryFLVs = {
    // This line is here to fix the issue described in https://quorumanalytics.atlassian.net/browse/NEO-4322
    // Check the comments in PR: https://github.com/QuorumUS/quorum-site/pull/37521
    [DjangIO.app.models.QuorumDataType.person.value]: OfficialFilterableListView,
    [DjangIO.app.models.QuorumDataType.statistic.value]: StatisticFilterableListView,
    [DjangIO.app.models.QuorumDataType.user.value]: UserFilterableListView,
    [DjangIO.app.models.QuorumDataType.legislator.value]: OfficialFilterableListView,
    [DjangIO.app.models.QuorumDataType.bulk_email.value]: BulkEmailFilterableListView,
    [DjangIO.app.models.QuorumDataType.campaign.value]: CampaignFilterableListView,
    [DjangIO.app.models.QuorumDataType.state.value]: StateSearchifyFLV,
    [DjangIO.app.models.QuorumDataType.custom_data.value]: CustomDataSearchifyFLV,
    [DjangIO.app.models.QuorumDataType.issue_management.value]: IssueManagementFilterableListView,
    [DjangIO.app.models.QuorumDataType.geoshape.value]: GeoShapeSearchifyFLV,
    [DjangIO.app.models.QuorumDataType.district.value]: DistrictSearchifyFLV,
    [DjangIO.app.models.QuorumDataType.blank_download.value]: () => <div />,
}

export const getCurrentRegionSearchOptions = () =>
    searchPageOptions
        .filter((item) => (item.enabledWithFeatureFlags ? isAnyFeatureEnabled(item.enabledWithFeatureFlags) : true))
        .filter(kseFilter)
        .filter(msaDatasetFilter)
        .filter(ymcaDatasetFilter)
        .filter((item) => (item.disabledWithFeatureFlags ? !isAnyFeatureEnabled(item.disabledWithFeatureFlags) : true))
        .filter((item) =>
            permissionFilter(item.quorumDataType, {
                regions: item.quorumDataType.supported_regions,
                useCurrentRegions: true,
            }),
        )
        // to accommodate officials+staff differences between EU and US, otherwise region
        // should be addressed in the QDT enum and not in quorumItems
        .filter((item) => (item.regions ? checkHasRegion(item.regions) : true))
        // to accommodate Global region and custom regions spanning US, EU, and other international regions
        // (e.g. this stops us from showing all three "Officials", "Staffers", _and_ "Officials & Staff" when spanning EU and US)
        .filter((item) => (item.excludedByRegions ? checkDoesNotHaveAnyRegion(item.excludedByRegions) : true))
        // For the LeadershipConnect organization account, Alert and Outbox Repicients should be hidden from
        // search page options.
        .filter(
            (item) =>
                Userdata.organization_id !== LEADERSHIP_CONNECT_ORG_ID ||
                ![DjangIO.app.models.QuorumDataType.alert, DjangIO.app.models.QuorumDataType.send_email].includes(
                    item.quorumDataType,
                ),
        )
        .sort((a, b) => sortBySearchOrder(a.quorumDataType, b.quorumDataType))

/**
 * Takes a QuorumDataType enum value and returns a boolean based on if the QDT is
 * among the QuorumDataTypes accessible as a Search dataset depending on the user's
 * current region.
 *
 * @param {QuorumDataType enum value}
 * @returns {boolean}
 */
export const isDatasetInSearch = (qdtValue) => {
    const allowedSearchQdts = getCurrentRegionSearchOptions().map((item) => item.quorumDataType.value)

    return allowedSearchQdts.includes(qdtValue)
}

export const getInitialSearch = () => {
    const searchOptions = getCurrentRegionSearchOptions()
    const userDefault = Userdata.default_search_data_type

    return searchOptions.find((page) => page.quorumDataType.value === userDefault) || searchOptions[0]
}

export const allAllowedSearchOptions = searchPageOptions
    .filter((item) => permissionFilter(item.quorumDataType, { regions: Userdata.allowed_regions_values }))
    .filter((item) => (item.regions ? checkAllowedRegion(item.regions) : true))
    .sort((a, b) => sortBySearchOrder(a.quorumDataType, b.quorumDataType))

/* eslint-enable */
